/**
 * useVisitTracker — Track every route change to /api/track/visit.
 *
 * - Generates persistent visitor_id in localStorage (12 maanden TTL)
 * - Generates per-tab session_id in sessionStorage
 * - Captures UTM-parameters from URL (utm_source, utm_medium, utm_campaign, utm_content, utm_term)
 * - Persists last-seen UTM in localStorage for subsequent registers/checkouts
 *
 * Usage: place inside <BrowserRouter> in App.js
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const VISITOR_KEY = 'im_visitor_id';
const SESSION_KEY = 'im_session_id';
const UTM_KEY = 'im_utm';

const genId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'v-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const getVisitorId = () => {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = genId();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return genId();
  }
};

const getSessionId = () => {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = genId();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return genId();
  }
};

const captureUtmFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
  };
  const hasAny = Object.values(utm).some(v => v);
  if (hasAny) {
    try {
      localStorage.setItem(UTM_KEY, JSON.stringify({ ...utm, captured_at: new Date().toISOString() }));
    } catch { /* ignore */ }
    return utm;
  }
  // Fallback: gebruik laatste opgeslagen UTM
  try {
    const stored = JSON.parse(localStorage.getItem(UTM_KEY) || '{}');
    return {
      utm_source: stored.utm_source || '',
      utm_medium: stored.utm_medium || '',
      utm_campaign: stored.utm_campaign || '',
      utm_content: stored.utm_content || '',
      utm_term: stored.utm_term || '',
    };
  } catch { return {}; }
};

export const getStoredUtm = () => {
  try {
    return JSON.parse(localStorage.getItem(UTM_KEY) || '{}');
  } catch { return {}; }
};

export const getStoredVisitorId = () => getVisitorId();

export const trackEvent = async (kind, meta = {}) => {
  try {
    const body = {
      visitor_id: getVisitorId(),
      kind,
      path: window.location.pathname,
      utm: captureUtmFromUrl(),
      meta,
    };
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch { /* ignore */ }
};

export default function useVisitTracker() {
  const location = useLocation();
  const lastPathRef = useRef(null);

  useEffect(() => {
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;

    // Skip admin/platform routes — we tracken alleen publieke pageviews
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/platform')) return;

    const body = {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      path: location.pathname,
      referrer: document.referrer || '',
      utm: captureUtmFromUrl(),
    };

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* ignore */ });
  }, [location.pathname]);

  return null;
}
